import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Modal } from '../UI/Modal/Modal'
import ReactSelectWrapper from '../FormUtils/ReactSelectWrapper'
import { DatePicker } from '../FormUtils/DatePicker'
import TimePicker from '../FormUtils/TimePicker'
import apiService from 'services/api'
import { getGrantsList } from 'services/grantsApi'
import { useAuth } from '../../context/authentication/useAuth'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useDataTableContext } from '../DataTable'
import { calculateEndDateFromOccurrences } from './utils'
import type { ReminderCalendarEvent } from 'models/reminders'

type QuickSchedule = '1_week' | '2_weeks' | '1_month' | '3_months'
type ReminderType =
  | 'one_off'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'annually'
  | 'custom'
type EndType = 'never' | 'date' | 'occurrences'

const reminderSchema = z
  .object({
    title: z
      .string()
      .max(50, 'Title must be 50 characters or less')
      .refine((v) => !!v, { message: 'Title is required' }),
    run_at_date: z.string().optional(),
    run_at_time: z.string().optional(),
    timezone: z.string().optional(),
    content: z.string().optional(),
    recipients: z
      .array(z.number())
      .min(1, 'You must select at least one recipient'),
    contact_company: z.number().optional(),
    door_proxy: z.number().optional(),
    quick_schedule: z
      .union([
        z.literal('1_week'),
        z.literal('2_weeks'),
        z.literal('1_month'),
        z.literal('3_months'),
      ])
      .optional(),
    reminder_type: z
      .union([
        z.literal('one_off'),
        z.literal('daily'),
        z.literal('weekly'),
        z.literal('monthly'),
        z.literal('quarterly'),
        z.literal('annually'),
        z.literal('custom'),
      ])
      .optional(),
    end_type: z
      .union([z.literal('never'), z.literal('date'), z.literal('occurrences')])
      .optional(),
    end_date: z.string().optional(),
    occurrence_count: z.number().min(1).max(365).optional(),
    custom_interval: z.number().min(1).max(100).optional(),
    custom_unit: z
      .union([z.literal('days'), z.literal('weeks'), z.literal('months')])
      .optional(),
    calendar_integration_ids: z.array(z.number()).optional(),
  })
  .refine(
    (v) => {
      // Either quick_schedule OR a date must be provided
      return !!v.quick_schedule || !!v.run_at_date
    },
    {
      message: 'Select a date or use a quick schedule option',
      path: ['run_at_date'],
    }
  )
  .superRefine((v, ctx) => {
    // If a date is provided (and not using quick schedule), ensure the combined date-time is in the future
    if (v.run_at_date && !v.quick_schedule) {
      const selectedDate = new Date(
        `${v.run_at_date}T${v.run_at_time || '09:00'}:00`
      )
      const now = new Date()
      if (selectedDate <= now) {
        // Just add a single issue to trigger form validation
        // The actual error display is handled by the dateTimeError computed value
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '', // Empty message since we show it separately
          path: ['run_at_date'],
        })
      }
    }

    // Validate end_date for recurring reminders
    if (v.reminder_type && v.reminder_type !== 'one_off' && v.run_at_date) {
      const runAtDate = new Date(v.run_at_date)

      // Validate end_date is after run_at_date when end_type is 'date'
      if (v.end_type === 'date' && v.end_date) {
        const endDate = new Date(v.end_date)

        if (endDate <= runAtDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date must be after the start date',
            path: ['end_date'],
          })
        }
      }

      // Validate calculated end_date from occurrences
      if (v.end_type === 'occurrences' && v.occurrence_count) {
        try {
          const calculatedEndDate = calculateEndDateFromOccurrences(
            runAtDate,
            v.reminder_type,
            v.occurrence_count,
            v.custom_interval,
            v.custom_unit
          )

          // Ensure calculated end date is after start date
          if (calculatedEndDate <= runAtDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                'Number of occurrences must result in an end date after the start date',
              path: ['occurrence_count'],
            })
          }
        } catch (error) {
          // Handle validation errors from calculateEndDateFromOccurrences
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              error instanceof Error
                ? error.message
                : 'Invalid recurrence settings',
            path: ['occurrence_count'],
          })
        }
      }
    }
  })

const reminderTypes = [
  { label: 'Once', value: 'one_off' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Annually', value: 'annually' },
  { label: 'Custom', value: 'custom' },
]

export type ReminderFormValues = z.infer<typeof reminderSchema>

// Props for the wrapper component used in tables
export interface ReminderModalForTableProps {
  open: boolean
  onClose: () => void
  data?: any[]
  isDoorsTable?: boolean
  chainProxyIdAccessorKey?: string
  doorProxyIdAccessorKey?: string
  onCreated?: () => void
}

// Wrapper component that uses table context to get selected IDs
export function ReminderModalForTable(props: ReminderModalForTableProps) {
  const {
    state: { rowSelection },
  } = useDataTableContext()

  const selectedRows = props.data?.filter((_, i) => rowSelection[i])

  // Determine company and door proxy IDs based on selection
  const selectedCompanyId = useMemo(() => {
    if (props.isDoorsTable || selectedRows?.length !== 1) return undefined

    const selectedItem = selectedRows[0]
    if (props.chainProxyIdAccessorKey && selectedItem) {
      const keys = props.chainProxyIdAccessorKey.split('.')
      return keys.reduce((acc, key) => acc?.[key], selectedItem)
    }
    return selectedItem?.id
  }, [selectedRows, props.isDoorsTable, props.chainProxyIdAccessorKey])

  const selectedDoorProxyId = useMemo(() => {
    if (!props.isDoorsTable || selectedRows?.length !== 1) return undefined

    const selectedItem = selectedRows[0]
    if (props.doorProxyIdAccessorKey && selectedItem) {
      const keys = props.doorProxyIdAccessorKey.split('.')
      return keys.reduce((acc, key) => acc?.[key], selectedItem)
    }
    return selectedItem?.id
  }, [selectedRows, props.isDoorsTable, props.doorProxyIdAccessorKey])

  return (
    <ReminderModal
      open={props.open}
      onClose={props.onClose}
      defaultCompanyId={selectedCompanyId}
      defaultDoorProxyId={selectedDoorProxyId}
      onCreated={props.onCreated}
    />
  )
}

export default function ReminderModal({
  open,
  onClose,
  defaultCompanyId,
  defaultDoorProxyId,
  defaultDealIds,
  onCreated,
  reminderId,
  onUpdated,
}: {
  open: boolean
  onClose: () => void
  defaultCompanyId?: number
  defaultDoorProxyId?: number
  defaultDealIds?: number[]
  onCreated?: () => void
  reminderId?: number
  onUpdated?: () => void
}) {
  const api = apiService()
  const { data: userOptions } = api.useGetUserOptions({ onlyActive: true })
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const isEditMode = !!reminderId

  // Create recipient options including "Me" option for current user
  const recipientOptions = useMemo(() => {
    if (!userOptions || !user) return []

    // Add "Me" option at the beginning for the current user
    const meOption = {
      label: 'Me',
      value: user.id,
      original: user,
    }

    // Filter out the current user from the regular list to avoid duplicates
    const otherUsers = userOptions.filter((u: any) => u.value !== user.id)

    return [meOption, ...otherUsers]
  }, [userOptions, user])

  // Fetch calendar integrations for the dropdown
  const { data: grantsData } = useQuery({
    queryKey: ['grants-with-calendar'],
    queryFn: ({ signal }) => getGrantsList({}, signal),
    enabled: open,
  })

  const calendarOptions = useMemo(() => {
    if (!grantsData?.results) return []
    return grantsData.results
      .filter(
        (g) => g.has_calendar && !g.is_expired && g.calendar_integration_id
      )
      .map((g) => ({
        label: g.email,
        value: g.calendar_integration_id as number,
      }))
  }, [grantsData])

  // Fetch reminder data for edit mode
  const { data: reminderData, isLoading: isLoadingReminder } = useQuery({
    queryKey: ['reminder', reminderId],
    queryFn: async ({ signal }) => {
      if (!reminderId) return null
      return api.getReminder(reminderId, signal)
    },
    enabled: isEditMode && open,
  })

  const editCalendarValues = useMemo(
    () =>
      reminderData?.calendar_events?.map((ce: ReminderCalendarEvent) => ({
        label: ce.email,
        value: ce.calendar_integration_id,
      })) ?? [],
    [reminderData]
  )

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
    reset,
    watch,
    setValue,
  } = useForm<ReminderFormValues>({
    resolver: zodResolver(reminderSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      content: '',
      recipients: user?.id ? [user.id] : [],
      contact_company: defaultCompanyId,
      door_proxy: defaultDoorProxyId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      run_at_time: '09:00',
      reminder_type: 'one_off' as ReminderType,
      end_type: 'never' as EndType,
    },
  })

  // Populate form when reminder data is loaded
  useEffect(() => {
    if (reminderData && isEditMode && recipientOptions.length > 0) {
      // Extract date and time from run_at
      if (reminderData.run_at) {
        const date = new Date(reminderData.run_at)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')

        setValue('run_at_date', `${year}-${month}-${day}`)
        setValue('run_at_time', `${hours}:${minutes}`)
      }

      setValue('title', reminderData.title)
      setValue('content', reminderData.content || '')

      // Map recipient objects to their IDs
      const recipientIds = reminderData.recipients?.map((r: any) => r.id) || []
      setValue('recipients', recipientIds)

      setValue('contact_company', reminderData.contact_company?.id)
      setValue('door_proxy', reminderData.door_proxy?.id)
      setValue(
        'timezone',
        reminderData.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone
      )

      // Set recurring fields
      setValue('reminder_type', reminderData.reminder_type || 'one_off')
      setValue('end_type', reminderData.end_type || 'never')
      if (reminderData.end_date) {
        const endDate = new Date(reminderData.end_date)
        const year = endDate.getFullYear()
        const month = String(endDate.getMonth() + 1).padStart(2, '0')
        const day = String(endDate.getDate()).padStart(2, '0')
        setValue('end_date', `${year}-${month}-${day}`)
      }
      if (reminderData.occurrence_count) {
        setValue('occurrence_count', reminderData.occurrence_count)
      }
      if (reminderData.custom_interval) {
        setValue('custom_interval', reminderData.custom_interval)
      }
      if (reminderData.custom_unit) {
        setValue('custom_unit', reminderData.custom_unit)
      }
    }
  }, [reminderData, isEditMode, setValue, recipientOptions])

  const runAt = useMemo(() => {
    const d = watch('run_at_date')
    const t = watch('run_at_time') || '09:00'
    if (!d) return undefined
    return new Date(`${d}T${t}:00`)
  }, [watch('run_at_date'), watch('run_at_time')])

  // Check if the selected date/time is in the past
  const dateTimeError = useMemo(() => {
    const date = watch('run_at_date')
    const time = watch('run_at_time') || '09:00'
    const quickSchedule = watch('quick_schedule')

    // Don't show error if using quick schedule or no date is set
    if (!date || quickSchedule) return null

    const selectedDateTime = new Date(`${date}T${time}:00`)
    const now = new Date()

    if (selectedDateTime <= now) {
      return 'Please select a future date and time for the reminder'
    }

    return null
  }, [watch('run_at_date'), watch('run_at_time'), watch('quick_schedule')])

  const onSubmit = handleSubmit(async (values) => {
    try {
      const payload: any = {
        title: values.title,
        recipients: values.recipients,
        timezone: values.timezone,
      }

      // Add optional fields only if they have values
      if (values.content !== undefined) {
        payload.content = values.content
      }

      // Only send company and door IDs during creation, not updates
      if (!isEditMode) {
        // In create mode, use form values first, then fall back to default props
        const companyId = values.contact_company || defaultCompanyId || null
        if (companyId) {
          payload.contact_company = companyId
        }

        const doorId = values.door_proxy || defaultDoorProxyId || null
        if (doorId) {
          payload.door_proxy = doorId
        }

        // Add deal IDs if provided in create mode
        if (defaultDealIds && defaultDealIds.length > 0) {
          payload.deals = defaultDealIds
        }
      }

      // Handle scheduling
      if (values.quick_schedule) {
        payload.quick_schedule = values.quick_schedule
      } else if (runAt) {
        payload.run_at = runAt.toISOString()
      }

      // Add recurring fields
      if (values.reminder_type && !isEditMode) {
        payload.reminder_type = values.reminder_type
      }

      if (values.reminder_type && values.reminder_type !== 'one_off') {
        // Only add recurring fields if it's not a one-off reminder
        if (values.end_type) {
          payload.end_type = values.end_type
        }

        if (values.end_type === 'date' && values.end_date) {
          payload.end_date = new Date(values.end_date).toISOString()
        }

        if (values.end_type === 'occurrences' && values.occurrence_count) {
          payload.occurrence_count = values.occurrence_count
        }

        if (values.reminder_type === 'custom') {
          if (values.custom_interval) {
            payload.custom_interval = values.custom_interval
          }
          if (values.custom_unit) {
            payload.custom_unit = values.custom_unit
          }
        }
      }

      // Add calendar integration IDs in create mode
      if (!isEditMode && values.calendar_integration_ids?.length) {
        payload.calendar_integration_ids = values.calendar_integration_ids
      }

      // Make API call based on mode
      if (isEditMode && reminderId) {
        const updatedReminder = await api.updateReminder(reminderId, payload)
        if (updatedReminder) {
          queryClient.setQueryData(['reminder', reminderId], updatedReminder)
        }
        toast.success('Reminder updated successfully')
        onUpdated?.()
      } else {
        await api.createReminder(payload)
        toast.success('Reminder created successfully')
        onCreated?.()
      }

      // Invalidate reminders table queries to refresh the data
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[]
          // Check if this is a reminders table query by looking at the tableKey
          return (
            typeof key[0] === 'string' &&
            (key[0].toLowerCase().includes('reminder') ||
              key[0].toLowerCase().includes('activity'))
          )
        },
      })

      reset()
      onClose()
    } catch (error) {
      console.error('Error saving reminder:', error)
      toast.error(
        isEditMode ? 'Failed to update reminder' : 'Failed to create reminder'
      )
    }
  })

  const handleQuickSchedule = (value: QuickSchedule) => {
    const today = new Date()
    const targetDate = new Date()

    switch (value) {
      case '1_week':
        targetDate.setDate(today.getDate() + 7)
        break
      case '2_weeks':
        targetDate.setDate(today.getDate() + 14)
        break
      case '1_month':
        targetDate.setMonth(today.getMonth() + 1)
        break
      case '3_months':
        targetDate.setMonth(today.getMonth() + 3)
        break
    }

    // Format as YYYY-MM-DD for the date input
    const year = targetDate.getFullYear()
    const month = String(targetDate.getMonth() + 1).padStart(2, '0')
    const day = String(targetDate.getDate()).padStart(2, '0')
    const formattedDate = `${year}-${month}-${day}`

    setValue('run_at_date', formattedDate, { shouldValidate: true })
    setValue('quick_schedule', undefined, { shouldValidate: true })
  }

  const quickButtons: { label: string; value: QuickSchedule }[] = [
    { label: '1 week', value: '1_week' },
    { label: '2 weeks', value: '2_weeks' },
    { label: '1 month', value: '1_month' },
    { label: '3 months', value: '3_months' },
  ]

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={isEditMode ? 'Edit Reminder' : 'Schedule Reminder'}
      description="Reminder emails will be sent at the selected time to the selected recipients, and can be viewed or edited on the Activity page."
      onAccept={onSubmit}
      acceptButtonText={isEditMode ? 'Update Reminder' : 'Schedule Reminder'}
      blockAccept={!isValid || isLoadingReminder}
      blockCancel={isLoadingReminder}
      loading={isSubmitting || isLoadingReminder}
      size="md"
    >
      {isLoadingReminder ? (
        <div className="flex flex-col gap-4 pr-2 animate-pulse">
          <div>
            <div className="h-4 w-16 bg-gray-200 rounded mb-1" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
          <div>
            <div className="h-4 w-16 bg-gray-200 rounded mb-1" />
            <div className="h-10 bg-gray-200 rounded" />
            <div className="grid grid-cols-4 gap-2 mt-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-8 bg-gray-200 rounded" />
              ))}
            </div>
          </div>
          <div>
            <div className="h-4 w-16 bg-gray-200 rounded mb-1" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
          <div>
            <div className="h-4 w-24 bg-gray-200 rounded mb-1" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
          <div>
            <div className="h-4 w-24 bg-gray-200 rounded mb-1" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
          <div>
            <div className="h-4 w-40 bg-gray-200 rounded mb-1" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
          <div>
            <div className="h-4 w-20 bg-gray-200 rounded mb-1" />
            <div className="h-28 bg-gray-200 rounded" />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4 pr-2">
          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Title*
            </label>
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <input
                  className="form-control"
                  placeholder="Reminder title"
                  autoFocus
                  {...field}
                />
              )}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">
                {errors.title.message}
              </p>
            )}
          </div>

          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Date*
            </label>
            <Controller
              control={control}
              name="run_at_date"
              render={({ field }) => (
                <DatePicker value={field.value} onChange={field.onChange} />
              )}
            />
            <div className="grid grid-cols-4 gap-2 mt-3">
              {quickButtons.map((b) => (
                <button
                  key={b.value}
                  type="button"
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 w-full"
                  onClick={() => handleQuickSchedule(b.value)}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Time
              <span className="italic text-sm font-normal"> - optional</span>
            </label>
            <Controller
              control={control}
              name="run_at_time"
              render={({ field }) => (
                <TimePicker
                  className="w-full"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Frequency
            </label>
            <Controller
              control={control}
              name="reminder_type"
              render={({ field }) => (
                <select
                  className="form-control"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    // Reset recurring fields when changing type
                    if (e.target.value === 'one_off') {
                      setValue('end_type', 'never')
                      setValue('end_date', undefined)
                      setValue('occurrence_count', undefined)
                      setValue('custom_interval', undefined)
                      setValue('custom_unit', undefined)
                    } else {
                      // Clear calendar selection for recurring reminders
                      setValue('calendar_integration_ids', [])
                    }
                    if (e.target.value === 'custom') {
                      // Set defaults for custom type
                      if (!watch('custom_unit')) {
                        setValue('custom_unit', 'days')
                      }
                      if (!watch('custom_interval')) {
                        setValue('custom_interval', 1)
                      }
                    }
                  }}
                  disabled={isEditMode}
                >
                  {reminderTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>

          {/* Recurring reminder configuration */}
          {watch('reminder_type') !== 'one_off' && (
            <div className="bg-surface-muted p-3 rounded">
              {/* Custom interval configuration */}
              {watch('reminder_type') === 'custom' && (
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-800 mb-1">
                    Repeat every
                  </label>
                  <div className="flex gap-2">
                    <Controller
                      control={control}
                      name="custom_interval"
                      render={({ field }) => (
                        <input
                          type="number"
                          className="form-control w-20"
                          placeholder="5"
                          min="1"
                          max="100"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value
                                ? parseInt(e.target.value)
                                : undefined
                            )
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="custom_unit"
                      render={({ field }) => (
                        <select className="form-control w-28" {...field}>
                          <option value="days">days</option>
                          <option value="weeks">weeks</option>
                          <option value="months">months</option>
                        </select>
                      )}
                    />
                  </div>
                  {(errors.custom_interval || errors.custom_unit) && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.custom_interval?.message ||
                        errors.custom_unit?.message}
                    </p>
                  )}
                </div>
              )}

              {/* Radio */}
              <label className="text-sm font-medium text-gray-800 mb-1">
                End
              </label>
              <div className="flex gap-2">
                <Controller
                  control={control}
                  name="end_type"
                  render={({ field }) => (
                    <>
                      <label className="flex items-center gap-2 cursor-pointer bg-white border border-gray-500 rounded px-3 py-1">
                        <input
                          type="radio"
                          value="never"
                          checked={field.value === 'never'}
                          className="accent-fb-green-900"
                          onChange={(e) => {
                            field.onChange(e.target.value)
                            setValue('end_date', undefined)
                            setValue('occurrence_count', undefined)
                          }}
                        />
                        <span className="text-sm">Never</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer bg-white border border-gray-500 rounded px-3 py-1">
                        <input
                          type="radio"
                          value="date"
                          checked={field.value === 'date'}
                          className="accent-fb-green-900"
                          onChange={(e) => {
                            field.onChange(e.target.value)
                            setValue('occurrence_count', undefined)
                          }}
                        />
                        <span className="text-sm">On</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer bg-white border border-gray-500 rounded px-3 py-1">
                        <input
                          type="radio"
                          value="occurrences"
                          checked={field.value === 'occurrences'}
                          className="accent-fb-green-900"
                          onChange={(e) => {
                            field.onChange(e.target.value)
                            setValue('end_date', undefined)
                          }}
                        />
                        <span className="text-sm">After</span>
                      </label>
                    </>
                  )}
                />
              </div>

              {/* End date selector */}
              {watch('end_type') === 'date' && (
                <div className="mt-2">
                  <Controller
                    control={control}
                    name="end_date"
                    render={({ field }) => (
                      <DatePicker
                        className="w-full"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {errors.end_date && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.end_date.message}
                    </p>
                  )}
                </div>
              )}

              {/* Occurrence count */}
              {watch('end_type') === 'occurrences' && (
                <div className="flex gap-2 mt-2">
                  <Controller
                    control={control}
                    name="occurrence_count"
                    render={({ field }) => (
                      <input
                        type="number"
                        className="form-control w-20"
                        placeholder="#"
                        min="1"
                        max="365"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value
                              ? parseInt(e.target.value)
                              : undefined
                          )
                        }
                      />
                    )}
                  />
                  <div className="flex items-center">
                    <span className="text-sm font-medium">Occurrences</span>
                  </div>
                  {errors.occurrence_count && (
                    <p className="text-red-500 text-sm mt-1 col-span-2">
                      {errors.occurrence_count.message}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Recipient(s)*
            </label>
            <Controller
              control={control}
              name="recipients"
              render={({ field }) => (
                <ReactSelectWrapper
                  isMulti
                  placeholder="Select recipients..."
                  value={recipientOptions.filter((u: any) =>
                    field.value?.includes(u.value)
                  )}
                  options={recipientOptions as any}
                  onChange={(v) =>
                    field.onChange((v as any[]).map((i) => (i as any).value))
                  }
                />
              )}
            />
            {errors.recipients && (
              <p className="text-red-500 text-sm mt-1">
                {errors.recipients.message}
              </p>
            )}
          </div>

          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Add to Calendar
              <span className="italic text-sm font-normal"> (optional)</span>
            </label>
            {isEditMode ? (
              <ReactSelectWrapper
                isMulti
                isDisabled
                placeholder="No calendars linked"
                value={editCalendarValues}
                options={editCalendarValues}
              />
            ) : (
              <Controller
                control={control}
                name="calendar_integration_ids"
                render={({ field }) => (
                  <ReactSelectWrapper
                    isMulti
                    isDisabled={
                      calendarOptions.length === 0 ||
                      watch('reminder_type') !== 'one_off'
                    }
                    placeholder={
                      watch('reminder_type') !== 'one_off'
                        ? 'Not available for recurring reminders'
                        : calendarOptions.length === 0
                          ? 'Connect a calendar in Settings'
                          : 'Select calendar(s)...'
                    }
                    value={calendarOptions.filter((o) =>
                      field.value?.includes(o.value)
                    )}
                    options={calendarOptions}
                    onChange={(v) =>
                      field.onChange((v as any[]).map((i) => (i as any).value))
                    }
                  />
                )}
              />
            )}
          </div>

          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Content
              <span className="italic text-sm font-normal"> (optional)</span>
            </label>
            <Controller
              control={control}
              name="content"
              render={({ field }) => (
                <textarea
                  className="form-control"
                  placeholder="Reminder details..."
                  rows={5}
                  {...field}
                />
              )}
            />
          </div>

          {/* Date/time validation error */}
          {dateTimeError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-red-600 text-sm flex items-center">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {dateTimeError}
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
